import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getUserByUsername } from '../services/authentikAPI';
import PhoneVerifyStep from './PhoneVerifyStep';
import EmailPendingStep from './EmailPendingStep';
import SetNewPasswordStep from './SetNewPasswordStep';
import TotpSetupStep from './TotpSetupStep';
import { isWebauthnSupported } from '../auth/webauthnHelpers';
import { navigateAway } from '../lib/tabCloseSuppression';

// Landing page for a bulk-invited account's claim link (see bulkUsers.js /
// claimAccount.js backend headers for the full design). Deliberately
// re-fetches /status as the single source of truth after every step
// instead of tracking its own "which step" state client-side — the
// account's own attributes durably reflect what's left, so a refresh
// mid-claim just picks up exactly where it left off, no separate gate
// mechanism needed (unlike Register.jsx, which had to build one).

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const WEBAUTHN_CEREMONY_URL = process.env.REACT_APP_WEBAUTHN_CEREMONY_URL || 'https://auth.bescomapps.com/webauthn-ceremony';

const page = {
  minHeight: '100vh',
  boxSizing: 'border-box',
  backgroundImage: 'url(/BESCOM_SSO_BG_Image.png)',
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const card = {
  background: 'rgba(255, 255, 255, 0.65)',
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  border: '1px solid rgba(255, 255, 255, 0.5)',
  borderRadius: '16px',
  padding: '48px 40px',
  width: '100%',
  maxWidth: '420px',
  boxShadow: '0 20px 60px rgba(0, 0, 0, 0.35)',
};

const btn = {
  width: '100%',
  padding: '14px',
  background: 'linear-gradient(135deg, #009FE3 0%, #0072B1 100%)',
  color: '#fff',
  border: 'none',
  borderRadius: '10px',
  fontSize: '16px',
  fontWeight: '600',
  cursor: 'pointer',
  marginTop: '8px',
};

const titles = {
  phone: 'Verify your phone number',
  email: 'Verify your email address',
  password: 'Set your password',
  totp: 'Set up your authenticator app',
  'mfa-choice': 'Choose how you\'ll sign in',
  complete: 'Account activated',
};

export default function ClaimAccount() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const token = searchParams.get('token');

  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [totpChallenge, setTotpChallenge] = useState(null);
  const [totpLoading, setTotpLoading] = useState(false);
  const [totpError, setTotpError] = useState(null);
  // ADDED (2026-09-20): "step 5" — the mandatory Authenticator/Passkey/SMS
  // choice, same as self-registration now offers (see Register.jsx /
  // MfaSetupRequired.jsx). mfaMode: null shows the 3-card picker itself;
  // 'totp' switches to the existing QR sub-flow below, unchanged.
  const [mfaMode, setMfaMode] = useState(null);
  // Which button is in flight — null | 'passkey' | 'code' | 'totp' — not just whether ANY of them is, so
  // clicking one doesn't flip the OTHER buttons' own busy label text too (real bug, seen live 2026-09-22:
  // choosing Passkey made the code button's label change to "Please wait…" even though it was never clicked).
  const [mfaBusy, setMfaBusy] = useState(null);

  const emailSentForRef = useRef(null);
  // Passkey claim: the ceremony page sends the person back here with the
  // account already done. `finishing` holds the screen on "Signing you in…"
  // while we try to sign them in, instead of flashing the "Go to Sign In"
  // fallback first. Tried once per page load.
  const finishTriedRef = useRef(false);
  const [finishing, setFinishing] = useState(false);

  useEffect(() => {
    if (!token) {
      setError('This activation link is missing its token.');
      setLoading(false);
      return;
    }
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  async function fetchStatus() {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/status?token=${encodeURIComponent(token)}`);
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'This activation link is invalid or has expired.');
        setStatus(null);
        return;
      }
      setStatus(data);
      if (data.done && !finishTriedRef.current) {
        finishTriedRef.current = true;
        await tryFinishPasskeyClaim();
      }
    } catch (e) {
      setError('Could not reach the account service.');
    } finally {
      setLoading(false);
    }
  }

  // Only succeeds right after a passkey ceremony THIS link started (the
  // backend requires a one-time marker stamped at that moment) — for anyone
  // else it's a 403 and they simply see the ordinary "Go to Sign In" screen.
  async function tryFinishPasskeyClaim() {
    setFinishing(true);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/finish`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token }),
      });
      if (!res.ok) return;
      const data = await res.json();
      if (data.done) await handleCompletion(data);
    } catch (e) {
      // fall through to the ordinary "Go to Sign In" screen
    } finally {
      setFinishing(false);
    }
  }

  const step = !status
    ? null
    : status.needsPhone ? 'phone'
    : status.needsEmail ? 'email'
    : status.needsPassword ? 'password'
    : status.needsMfaChoice ? (mfaMode === 'totp' ? 'totp' : 'mfa-choice')
    : 'complete';

  // Email step: send the confirmation link once per token when this step
  // is first reached — not on every status re-fetch (e.g. the poll below).
  useEffect(() => {
    if (step !== 'email' || emailSentForRef.current === token) return;
    emailSentForRef.current = token;
    fetch(`${API_BASE}/auth/claim/send-email-confirmation`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token }),
    }).catch(() => {
      // best-effort — the Resend button in EmailPendingStep covers this
    });
  }, [step, token]);

  // Polls while the email step is showing — clicking the link (in
  // whatever tab/device the person checks their inbox from) clears
  // attributes.pending_email, which is the only signal this tab has that
  // it happened. Same pattern as Register.jsx's own polling.
  useEffect(() => {
    if (step !== 'email' || !status?.username) return;
    let cancelled = false;
    const interval = setInterval(async () => {
      try {
        const data = await getUserByUsername(status.username);
        if (!cancelled && data && !data.attributes?.pending_email) {
          fetchStatus();
        }
      } catch (e) {
        // transient network issue — keep polling
      }
    }, 4000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, status?.username]);

  async function handleResendEmail() {
    try {
      const res = await fetch(`${API_BASE}/auth/claim/send-email-confirmation`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      return res.ok;
    } catch (e) {
      return false;
    }
  }

  async function handleSetPassword(password) {
    const res = await fetch(`${API_BASE}/auth/claim/set-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, password }),
    });
    const data = await res.json();
    return res.ok ? { ok: true } : { error: data.error };
  }

  async function handleStartTotp() {
    setTotpLoading(true);
    setTotpError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/setup-totp`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTotpError(data.error || 'Could not start authenticator setup.');
        return;
      }
      setTotpChallenge(data);
    } catch (e) {
      setTotpError('Could not reach the account service.');
    } finally {
      setTotpLoading(false);
    }
  }

  async function handleChoosePasskey() {
    setMfaBusy('passkey');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/start-webauthn-ceremony`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!data.token) {
        setError(data.error || 'Could not start passkey setup. Please try again.');
        setMfaBusy(null);
        return;
      }
      // No pre-existing session to hand off yet (unlike self-registration's
      // own passkey choice) — land back on THIS same page, where fetchStatus
      // will see the confirmed device and move to step==='complete'. See
      // start-webauthn-ceremony's own backend header for why this is a
      // one-extra-click tradeoff rather than new handoff-token plumbing.
      const redirectBack = encodeURIComponent(`${window.location.origin}/claim-account?token=${encodeURIComponent(token)}`);
      navigateAway(`${WEBAUTHN_CEREMONY_URL}?token=${encodeURIComponent(data.token)}&redirect=${redirectBack}`);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMfaBusy(null);
    }
  }

  // One-time code — goes to the phone if the account has one, else the
  // email (backend lib/attributeMfa.js). No Authentik ceremony, so unlike a
  // passkey this completes and signs in immediately.
  async function handleChooseCode() {
    setMfaBusy('code');
    setError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/set-code-mfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Could not set up code sign-in. Please try again.');
        setMfaBusy(null);
        return;
      }
      await handleCompletion(data);
    } catch (e) {
      setError('Could not reach the account service. Please try again.');
      setMfaBusy(null);
    }
  }

  async function handleConfirmTotp({ code }) {
    setTotpLoading(true);
    setTotpError(null);
    try {
      const res = await fetch(`${API_BASE}/auth/claim/setup-totp/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setTotpError(data.error || 'Incorrect code. Please try again.');
        return;
      }
      if (data.done) {
        await handleCompletion(data);
        return;
      }
      // A wrong code re-shows the same TOTP stage (not a redirect), same
      // shape registration.js's own /step returns on a wrong code — see
      // Register.jsx's submitStep. This was previously unhandled here, so
      // a genuine "wrong code" response fell through to the generic
      // fallback message below instead of showing the real error.
      if (data.errors) {
        const firstKey = Object.keys(data.errors)[0];
        setTotpError(data.errors[firstKey]?.[0]?.string || 'Incorrect code. Please try again.');
        return;
      }
      setTotpError('Unexpected response from the identity provider. Please try again.');
    } catch (e) {
      setTotpError('Could not reach the account service.');
    } finally {
      setTotpLoading(false);
    }
  }

  async function handleCompletion(data) {
    if (data.authenticated) {
      // bescom_token is now set as an httpOnly cookie by the backend's
      // response itself (see AuthContext.js's own header comment) — no
      // client-side storage step needed anymore. data.authenticated is
      // the boolean signal that a real session was established (Phase 3
      // dropped the raw token from the response body).
      if (!data.profileComplete) {
        localStorage.setItem('bescom_needs_profile', '1');
      }
      if (data.sessionSyncUrl) {
        localStorage.setItem('bescom_needs_session_sync', '1');
      }
      // skipRefresh: this token was just minted a few lines above — see
      // AuthContext.js's own comment on resolveUser's skipRefresh param.
      await refreshUser({ skipRefresh: true });
      navigate(data.profileComplete ? '/dashboard' : '/complete-profile');
    } else {
      navigate('/', { state: { justRegistered: true } });
    }
  }

  return (
    <div style={page}>
      <div style={card}>
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <img src="/bescom-logo-ui.png" alt="BESCOM" style={{ width: '80px', marginBottom: '16px' }} />
          <h1 style={{ fontSize: '22px', fontWeight: '700', color: '#1a1a1a', margin: '0 0 8px' }}>
            {step ? titles[step] : 'Activate your account'}
          </h1>
          {status?.username && (
            <p style={{ fontSize: '14px', color: '#666', margin: 0 }}>{status.username}</p>
          )}
        </div>

        {error && (
          <div style={{
            background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
            borderRadius: '8px', fontSize: '13px', textAlign: 'center',
          }}>
            {error}
          </div>
        )}

        {loading && !status && !error && (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Loading…</p>
        )}

        {step === 'phone' && (
          <PhoneVerifyStep
            phone={status.phone}
            username={status.username}
            onVerified={fetchStatus}
          />
        )}

        {step === 'email' && (
          <EmailPendingStep email={status.maskedEmail} onResend={handleResendEmail} />
        )}

        {step === 'password' && (
          <SetNewPasswordStep
            onSubmit={handleSetPassword}
            onSuccess={fetchStatus}
            description="Choose a password for your new BESCOM account."
            submitLabel="Set Password"
          />
        )}

        {step === 'mfa-choice' && (
          <div>
            <p style={{ textAlign: 'center', color: '#666', fontSize: '14px', marginBottom: '20px', lineHeight: 1.5 }}>
              Choose a sign-in method — you'll need it every time you sign in, so pick whichever is easiest.
            </p>
            <button
              type="button"
              onClick={() => { setMfaBusy('totp'); setMfaMode('totp'); handleStartTotp(); }}
              disabled={!!mfaBusy}
              style={{ ...btn, opacity: mfaBusy ? 0.6 : 1, cursor: mfaBusy ? 'not-allowed' : 'pointer' }}
            >
              📱 Use an Authenticator App
            </button>
            {isWebauthnSupported() && (
              <button type="button" onClick={handleChoosePasskey} disabled={!!mfaBusy} style={{ ...btn, opacity: mfaBusy ? 0.6 : 1, cursor: mfaBusy ? 'not-allowed' : 'pointer' }}>
                {mfaBusy === 'passkey' ? 'Redirecting…' : '🔑 Use a Passkey'}
              </button>
            )}
            {status.codeChannel && (
              <button type="button" onClick={handleChooseCode} disabled={!!mfaBusy} style={{ ...btn, opacity: mfaBusy ? 0.6 : 1, cursor: mfaBusy ? 'not-allowed' : 'pointer' }}>
                {mfaBusy === 'code' ? 'Please wait…' : '🔐 Phone/Email OTP'}
              </button>
            )}
          </div>
        )}

        {step === 'totp' && (
          <div>
            {totpError && (
              <div style={{
                background: '#FDECEC', color: '#B3261E', padding: '10px 14px',
                borderRadius: '8px', fontSize: '13px', marginBottom: '16px',
              }}>
                {totpError}
              </div>
            )}
            {totpLoading && !totpChallenge && (
              <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Please wait…</p>
            )}
            {totpChallenge?.raw?.config_url && (
              <TotpSetupStep
                configUrl={totpChallenge.raw.config_url}
                loading={totpLoading}
                onSubmit={handleConfirmTotp}
                description="This is the last step — scan the code with your authenticator app."
                mandatory
              />
            )}
            <button
              type="button"
              onClick={() => { setMfaMode(null); setMfaBusy(null); setTotpChallenge(null); setTotpError(null); }}
              disabled={totpLoading}
              style={{ ...btn, background: 'none', border: '1px solid #D3D1C7', color: '#666', cursor: totpLoading ? 'not-allowed' : 'pointer' }}
            >
              Choose a different method
            </button>
          </div>
        )}

        {step === 'complete' && finishing && (
          <p style={{ textAlign: 'center', color: '#666', fontSize: '14px' }}>Signing you in…</p>
        )}

        {step === 'complete' && !finishing && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '40px', marginBottom: '16px' }}>✅</div>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '16px' }}>
              Your account is fully set up.
            </p>
            <button onClick={() => navigate('/')} style={btn}>
              Go to Sign In
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
